import { useEffect, useState } from "react";
import {
	Alert,
	Box,
	Dialog,
	DialogActions,
	DialogContent,
	Divider,
	Grid2,
	MenuItem,
	TextField,
} from "@mui/material";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import {
	DetailHeader,
	DetailSection,
} from "@/controls/detail-panel/DetailPanel";
import { FooterModalButtons } from "@/controls/buttons/FooterModalButtons";
import type { PassStatusOption, RollsProductionRow } from "../models/Rollshop";
import {
	COMPACT_DIALOG_SX,
	COMPACT_OPTION_SX,
	EDITABLE_FIELD_SX,
} from "./compactDialog";
import { ReadOnlyField } from "./ReadOnlyField";

const tonnage = (value: number | null) => (value ?? 0).toFixed(2);

interface Props {
	row: RollsProductionRow | null;
	rollsetName: string;
	passStatus: PassStatusOption[];
	isSaving: boolean;
	error: string;
	onClose: () => void;
	onSave: (row: RollsProductionRow, statusCode: string) => void;
}

export const PassConditionModal = ({
	row,
	rollsetName,
	passStatus,
	isSaving,
	error,
	onClose,
	onSave,
}: Props) => {
	const [statusCode, setStatusCode] = useState("");
	// Keeps the last pass on screen while the dialog fades out.
	const [shown, setShown] = useState(row);

	useEffect(() => {
		if (!row) return;
		setShown(row);
		setStatusCode(row.PassStatusCode ?? "");
	}, [row]);

	const changed =
		!!row && statusCode !== "" && statusCode !== row.PassStatusCode;

	return (
		<Dialog open={row !== null} onClose={onClose} maxWidth="xs" fullWidth>
			<DetailHeader
				title={`Pass Condition ${rollsetName}`}
				isSaving={isSaving}
				onClose={onClose}
			/>
			<form
				onSubmit={(e) => {
					e.preventDefault();
					if (row && changed) onSave(row, statusCode);
				}}
			>
				<DialogContent sx={{ p: 2, ...COMPACT_DIALOG_SX }}>
					<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
						<DetailSection title="Pass" icon={<FormatListNumberedIcon />}>
							<Grid2 container spacing={1.5} sx={{ pt: 0.5 }}>
								<Grid2 size={6}>
									<ReadOnlyField
										label="#"
										value={shown?.PassPosition?.toString()}
										numeric
									/>
								</Grid2>
								<Grid2 size={6}>
									<ReadOnlyField label="Pass" value={shown?.PassName} />
								</Grid2>
								<Grid2 size={6}>
									<ReadOnlyField label="Type" value={shown?.PassTypeName} />
								</Grid2>
								<Grid2 size={6}>
									<ReadOnlyField label="Shape" value={shown?.PassShapeName} />
								</Grid2>
								<Grid2 size={6}>
									<ReadOnlyField
										label="Scheduled"
										value={tonnage(shown?.ScheduledTonnage ?? null)}
										numeric
									/>
								</Grid2>
								<Grid2 size={6}>
									<ReadOnlyField
										label="Rolled"
										value={tonnage(shown?.RolledTonnage ?? null)}
										numeric
									/>
								</Grid2>
								<Grid2 size={6}>
									<ReadOnlyField
										label="Last Change"
										value={shown?.PassLastChangeReasonName}
									/>
								</Grid2>
								<Grid2 size={6}>
									<TextField
										select
										label="Status"
										value={statusCode}
										onChange={(e) => setStatusCode(e.target.value)}
										fullWidth
										size="small"
										autoFocus
										slotProps={{ inputLabel: { shrink: true } }}
										sx={EDITABLE_FIELD_SX}
									>
										{passStatus.map((s) => (
											<MenuItem
												key={s.Code}
												value={s.Code}
												sx={COMPACT_OPTION_SX}
											>
												{s.Name}
											</MenuItem>
										))}
									</TextField>
								</Grid2>
							</Grid2>
						</DetailSection>
						{error && <Alert severity="error">{error}</Alert>}
					</Box>
				</DialogContent>
				<Divider />
				<DialogActions sx={{ px: 2, py: 1.5 }}>
					<FooterModalButtons
						isSubmitLoading={isSaving}
						disabled={!changed}
						onHide={onClose}
						buttonLabel="Save"
					/>
				</DialogActions>
			</form>
		</Dialog>
	);
};
